import { useSyncExternalStore } from 'react';

import type { Schema } from '@/shared/api/contract';

import { type SessionUser, useSessionUser } from './currentUser';

/**
 * Profiles (contract v10.47, ADR 0042 of the back): what a person sees of prices,
 * customers, quotes and the supplier documents. The back decides; the session carries the
 * answer in `capabilities`, and the front hides what is not there. **A courtesy, not the
 * guard**: the API answers 403 to an area the profile does not include and leaves empty
 * the fields it may not see, whatever this module says.
 */
export type Capability = Schema<'Capability'>;
export type UserProfile = Schema<'UserProfile'>;

/** Every capability, for a back older than v10.47: before the profiles everybody saw
 * everything, and hiding screens the server still serves would only break them. Also for
 * no session at all, which under `RequireAuth` never happens (every screen that asks
 * renders after the session is there): the API decides either way, and a screen with
 * nothing to go on draws what it drew before the profiles instead of guessing. */
const EVERY_CAPABILITY: readonly Capability[] = [
  'documents',
  'see_max_discount',
  'see_purchase',
  'manage_documents',
  'clients',
  'quotes',
  'tariff_quotes',
];

export const PROFILE_LABELS: Record<UserProfile, string> = {
  direccion: 'Dirección y compras',
  tienda: 'Tienda',
  comercial: 'Comercial',
};

/** One line under each option of the picker in Usuarios: what changes for the person. */
export const PROFILE_HINTS: Record<UserProfile, string> = {
  direccion: 'Lo ve todo: compra, margen y descuento máximo',
  tienda: 'Catálogos, PVP, descuento máximo, clientes y presupuestos desde las tarifas; sin compra',
  comercial: 'Solo catálogos, PVP y descuento habitual',
};

export const PROFILE_ORDER: readonly UserProfile[] = ['direccion', 'tienda', 'comercial'];

/**
 * Each capability as «Qué ve cada perfil» says it (contract v10.58). Which profile has
 * which comes from `GET /api/admin/profiles`, the same table the API enforces; only the
 * words live here. A capability this front does not know shows as it came.
 */
export const CAPABILITY_LABELS: Record<Capability, string> = {
  documents: 'Catálogos y tarifas: PVP y descuento habitual',
  see_max_discount: 'Descuento máximo',
  see_purchase: 'Lo que paga Cubas: compra, neto y margen',
  manage_documents: 'Subir tarifas y catálogos y cambiar condiciones',
  clients: 'Clientes',
  tariff_quotes: 'Presupuestos desde las tarifas',
  quotes: 'Presupuestos desde ofertas de proveedor',
};

/** The order of the rows of «Qué ve cada perfil»: from what everybody has to what only
 * direction has. */
export const CAPABILITY_ORDER: readonly Capability[] = [
  'documents',
  'see_max_discount',
  'clients',
  'tariff_quotes',
  'quotes',
  'see_purchase',
  'manage_documents',
];

/** What each profile allows for a back older than v10.58, which does not say it: the
 * table of ADR 0042 as it stood when this was written. */
export const FALLBACK_CAPABILITIES: Record<UserProfile, readonly Capability[]> = {
  direccion: EVERY_CAPABILITY,
  tienda: ['documents', 'see_max_discount', 'clients', 'tariff_quotes'],
  comercial: ['documents'],
};

export function capabilitiesOf(user: SessionUser | undefined): ReadonlySet<Capability> {
  return new Set(user?.capabilities ?? EVERY_CAPABILITY);
}

/** Subscribed: a nav item or a button that depends on the profile re-renders when the
 * session changes (an administrator changed it and the session refreshed). */
export function useCan(capability: Capability): boolean {
  return capabilitiesOf(useSessionUser()).has(capability);
}

/** Any of `capabilities`: Presupuestos opens with `quotes` or with `tariff_quotes`
 * (contract v10.52), the shop having only the second. */
export function hasAny(
  capabilities: ReadonlySet<Capability>,
  wanted: Capability | readonly Capability[],
): boolean {
  return (typeof wanted === 'string' ? [wanted] : wanted).some((each) => capabilities.has(each));
}

/** The quotes a person makes from the tariffs (contract v10.52, ADR 0046 of the back):
 * «Añadir» in the search, «Pegar lista» in the editor. */
export const QUOTE_CAPABILITIES: readonly Capability[] = ['quotes', 'tariff_quotes'];

// --------------------------------------------------------------------------- //
// Counter mode
// --------------------------------------------------------------------------- //

/**
 * «Modo mostrador» (plan, «Perfiles»): whoever may see what Cubas pays hides it, with the
 * maximum discount and the margin, while a customer looks at the screen, until they press
 * it again. This browser's only: kept in `localStorage` so a reload in front of the
 * customer does not show it all again, and a storage that refuses (a private window) only
 * means it does not survive the reload. Not a permission: the person may see it.
 */
const COUNTER_MODE_KEY = 'cubas.modo-mostrador';
const listeners = new Set<() => void>();
let counterMode = readCounterMode();

function readCounterMode(): boolean {
  try {
    return globalThis.localStorage?.getItem(COUNTER_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setCounterMode(on: boolean): void {
  counterMode = on;
  try {
    if (on) globalThis.localStorage?.setItem(COUNTER_MODE_KEY, '1');
    else globalThis.localStorage?.removeItem(COUNTER_MODE_KEY);
  } catch {
    // Kept in memory for this tab; see above.
  }
  for (const listener of listeners) listener();
}

/** Another tab of this browser pressed it: this one follows, so the customer does not see
 * the prices in the tab nobody touched. */
function onStorage(event: StorageEvent): void {
  if (event.key !== COUNTER_MODE_KEY && event.key !== null) return;
  counterMode = readCounterMode();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) globalThis.addEventListener?.('storage', onStorage);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) globalThis.removeEventListener?.('storage', onStorage);
  };
}

export function useCounterMode(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => counterMode,
    () => false,
  );
}

/** Whether this person has anything for the counter mode to hide: the purchase, or the
 * maximum discount of the tariffs (contract v10.48), which the shop sees. A button that
 * hides nothing is a button that teaches people not to trust it. */
export function useHasHiddenPrices(): boolean {
  const capabilities = capabilitiesOf(useSessionUser());
  return capabilities.has('see_purchase') || capabilities.has('see_max_discount');
}

/**
 * What a screen shows of the prices behind the counter: the capability, unless the
 * counter mode hides it right now.
 */
export function useShowsPurchase(): boolean {
  const can = useCan('see_purchase');
  const hidden = useCounterMode();
  return can && !hidden;
}

export function useShowsMaxDiscount(): boolean {
  const can = useCan('see_max_discount');
  const hidden = useCounterMode();
  return can && !hidden;
}
